import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';

import isYearFixed from 'Common/Utils/isYearFixed';
import { getTypes, getTeams } from './service';
import errorCb from 'Common/Utils/errorCb';

import View from './TeamTypeFilter.view';

const emptyTeams = new Set([]);
const emptyTypes = new Set([]);

const TeamTypeFilter = ({
    open,
    togglePanel,
    isFiltering,
    setIsFiltering,
    grouping,
    year,
    numberOfPreviousYears,
    numberOfFutureYears,
    updateYear,
    startNewFilter,
    onClear,
    onSubmit
}) => {
	const [startWith, setStartWith] = useState('');
	const [types, updateTypes] = useState(emptyTypes);
	const [allTypes, setAllTypes] = useState([]);
    const [typesReady, setTypesReady] = useState(false);
	const [typesForGrouping, setTypesForGrouping] = useState([]);
	const [teams, updateTeams] = useState(emptyTeams);
	const [allTeams, setAllTeams] = useState([]);
    const [teamsReady, setTeamsReady] = useState(false);
	const [teamsForGrouping, setTeamsForGrouping] = useState([]);
	const [groupingTeamData, setGroupingTeamData] = useState({});
	const [refining, setRefining] = useState(false);
	const dispatch = useDispatch();
    const showStartWith = !!year && typesReady && teamsReady;

	const handleYearUpdate = useCallback(value => {
		if (value > 0) {
			updateYear(value);
		} else {
			updateYear('');
		}
		clearTeamsAndTypes();
	// eslint-disable-next-line
	}, []);

	const setFiltering = () => {
		if (!isFiltering) {
			setIsFiltering && setIsFiltering(true);
		}
	};

	useEffect(() => {
		let didCancel = false;
		const apiGetTypes = async () => {
			const groupingTypes = await getTypes(year, grouping, null, errorCb([], `Error fetching Types for ${grouping}`, dispatch));
			if (!didCancel) {
                setAllTypes(groupingTypes);
				setTypesForGrouping(groupingTypes);
                setTypesReady(true);
			}
		};
		const apiGetTeams = async () => {
			const teamData = await getTeams(year, grouping, null, true, errorCb([], `Error fetching Teams for ${grouping}`, dispatch));
			if (!didCancel) {
				const teamDataObj = teamData.reduce((_teamDataObj, currentTeam) => {
					const { team, teamId } = currentTeam;
					_teamDataObj[team] = teamId;
					return _teamDataObj;
				}, {});

				setGroupingTeamData(teamDataObj);
				const groupingTeams = Object.keys(teamDataObj);
                setAllTeams(groupingTeams);
				setTeamsForGrouping(groupingTeams);
                setTeamsReady(true);
			}
		};
		if (year && grouping) {
            setTypesReady(false);
            setTeamsReady(false);
			updateTypes(emptyTypes);
			updateTeams(emptyTeams);
			apiGetTypes();
			apiGetTeams();
		}
		return () => {
			didCancel = true;
		};
	// eslint-disable-next-line
	}, [year, grouping]);

	const getUpdatedSelection = (options, selectedOptions) => {
		const optionSet = new Set(options);
		const updatedSelection = new Set([]);
		selectedOptions.forEach(item => {
			if (optionSet.has(item)) {
				updatedSelection.add(item);
			}
		});
		return updatedSelection;
	};

	useEffect(() => {
		let didCancel = false;
		setFiltering();
		const apiGetTeams = async () => {
			const teamsForTypes = await getTeams(year, grouping, [...types], false, errorCb([], 'Error fetching Teams for specified Types', dispatch));
			if (!didCancel) {
				
				const teamDataObj = teamsForTypes.reduce((_teamDataObj, currentTeam) => {
					const { team, teamId } = currentTeam;
					_teamDataObj[team] = teamId;
					return _teamDataObj;
				}, {});
				
				const groupingTeams = Object.keys(teamDataObj);
				setAllTeams(groupingTeams);
				const selectedTeams = refining ? getUpdatedSelection(groupingTeams, teams) : new Set(groupingTeams);
				updateTeams(selectedTeams);
			}
		};
		if (startWith === 'Type') {
			if (types.size === 0) {
				setAllTeams([]);
				updateTeams(emptyTeams);
			} else {
				apiGetTeams();
			}
		}
		return () => {
			didCancel = true;
		};
	// eslint-disable-next-line
	}, [types]);

	useEffect(() => {
		let didCancel = false;
		setFiltering();
		const apiGetTypes = async () => {
		
			//	Make the values API friendly.
			const teamsMap = [...teams].map(x => x.split(') - ')[1]);
			const typesForTeams = await getTypes(year, grouping, teamsMap, errorCb([], 'Error fetching Types for specified Teams', dispatch));
			
			if (!didCancel) {
				setAllTypes(typesForTeams);
				const selectedTypes = refining ? getUpdatedSelection(typesForTeams, types) : new Set(typesForTeams);
				updateTypes(selectedTypes);
			}
		};
		if (startWith === 'Team') {
			if (teams.size === 0) {
				setAllTypes([]);
				updateTypes(emptyTypes);
			} else {
				apiGetTypes();
			}
		}
		return () => {
			didCancel = true;
		};
	// eslint-disable-next-line
	}, [teams]);

	useEffect(() => {
		setFiltering();
		if (startWith === 'Team') {
			setAllTeams(teamsForGrouping);
			setAllTypes([]);
		} else if (startWith === 'Type') {
			setAllTypes(typesForGrouping);
			setAllTeams([]);
		} else {
			setAllTypes([]);
			setAllTeams([]);
		}
		updateTypes(emptyTypes);
		updateTeams(emptyTeams);
        setRefining(false);
	// eslint-disable-next-line
	}, [startWith]);

	useEffect(() => {
		if (startWith === 'Team') {
			if (refining) {
				updateTypes(emptyTypes);
			} else {
				updateTypes(new Set(allTypes));
			}
		} else if (startWith === 'Type') {
			if (refining) {
				updateTeams(emptyTeams);
			} else {
				updateTeams(new Set(allTeams));
			}
		}
	// eslint-disable-next-line
	}, [refining]);

	const handleNewFilter = () => {
		const fixedYear = isYearFixed(grouping);
		if (!fixedYear) {
			updateYear('');
		}
		clearTeamsAndTypes();
		startNewFilter && startNewFilter();
	};

	const clearTeamsAndTypes = () => {
		setStartWith('');
		updateTypes(emptyTypes);
		setAllTypes(typesForGrouping);
		updateTeams(emptyTeams);
		setAllTeams(teamsForGrouping);
		setRefining(false);

        onClear();
	};

    const handleGo = useCallback(() => {
		const teamIds = Array.from(teams).reduce((_teamIds, currentTeam) => {
			_teamIds.push(groupingTeamData[currentTeam]);
			return _teamIds;
		}, []);
		const numberOfTeamsInGrouping = Object.keys(groupingTeamData).length;
        const filteredTypes = types.size === typesForGrouping.length ? null : [...types];
        const filteredTeamIds = teams.size === numberOfTeamsInGrouping ? null : [...teamIds];
        onSubmit(year, filteredTypes, filteredTeamIds);
    }, [onSubmit, year, types, teams, typesForGrouping, groupingTeamData]);

	return (
		<View
			open={open}
			togglePanel={togglePanel}
			grouping={grouping}
			updateYear={handleYearUpdate}
            year={year}
            numberOfPreviousYears={numberOfPreviousYears}
            numberOfFutureYears={numberOfFutureYears}
			startWith={startWith}
			setStartWith={setStartWith}
            showStartWith={showStartWith}
			types={types}
			teams={teams}
			allTypes={allTypes}
			allTeams={allTeams}
			updateTypes={updateTypes}
			updateTeams={updateTeams}
			handleNewFilter={handleNewFilter}
			handleGo={handleGo}
			refining={refining}
			setRefining={setRefining}
		/>
	);
};

TeamTypeFilter.propTypes = {
	open: PropTypes.bool.isRequired,
	togglePanel: PropTypes.func.isRequired,
	isFiltering: PropTypes.bool,
	setIsFiltering: PropTypes.func,
	grouping: PropTypes.string.isRequired,
    year: PropTypes.string,
    numberOfPreviousYears: PropTypes.number,
    numberOfFutureYears: PropTypes.number,
	updateYear: PropTypes.func.isRequired,
	startNewFilter: PropTypes.func,
    onClear: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired
};

export default TeamTypeFilter;
